import { type ThreeEvent } from '@react-three/fiber';
import { type FC, useCallback, useEffect, useMemo, useRef, memo } from 'react';
import * as THREE from 'three';
import type { SphericalImageViewerProps, PointPickResult } from '../../types';
import { ViewerLayer } from '../ViewerLayer';
import { RENDER_ORDER_IMAGE_VIEWER } from '../RenderOrder';
import { useImageMaterial } from '../Shaders/useImageMaterial';
import { useTextureLoader } from '../Loaders/useTextureLoader';

export interface SphericalImageViewerExtendedProps extends SphericalImageViewerProps {
  pickPointCloud: (nativeEvent: PointerEvent | MouseEvent) => PointPickResult | undefined;
  viewerSphere: THREE.SphereGeometry;
}

export const SphericalImageViewer: FC<SphericalImageViewerExtendedProps> = memo(
  ({
    url,
    requestHeader,
    position,
    rotation,
    imageYawOffset,
    setNextCurrentImage,
    opacity,
    onClick,
    pickPointCloud,
    viewerSphere,
    // default is 10 meters sphere radius allows to show 'things (meshes, point clouds)' inside
    sphereRadius = 10,
    brightness,
    contrast,
  }) => {
    const { texture } = useTextureLoader({ url, requestHeader });

    const updatedTexture = useMemo(() => {
      if (texture != null) {
        // more crispy image with LinearFilter and without mipmaps
        texture.generateMipmaps = false;
        texture.minFilter = THREE.LinearFilter;
        texture.wrapS = THREE.RepeatWrapping;
        texture.repeat.x = -1;
        texture.needsUpdate = true;
      }
      return texture;
    }, [texture]);

    const imageMaterial = useImageMaterial({
      map: updatedTexture,
      brightness,
      contrast,
      side: THREE.BackSide,
      transparent: true,
      toneMapped: false,
      opacity,
    });

    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        // Esc key
        const { altKey, ctrlKey, metaKey, shiftKey } = event;
        if (event.keyCode === 27 && !altKey && !ctrlKey && !metaKey && !shiftKey) {
          setNextCurrentImage(undefined);
          event.stopPropagation();
        }
      };
      window.addEventListener('keydown', handleKeyDown);

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, [setNextCurrentImage]);

    const meshReference = useRef<THREE.Mesh | null>(null);

    const handleMesh = useCallback((mesh: THREE.Mesh | null) => {
      meshReference.current = mesh;
      mesh?.layers.enable(ViewerLayer.AutoPickable);
    }, []);

    // Three.js Euler uses intrinsic Tait-Bryan angles.
    // `rotation` contains extrinsic Tait-Bryan angles,
    // as it provided in `${inspection}/spatial/locations/camera-positions-platform-grid.csv`
    // Quote: "Any extrinsic rotation is equivalent to an intrinsic rotation by the same angles but with inverted order of elemental rotations, and vice-versa."
    // So THREE.Euler(roll, pitch, yaw, 'ZYX') should construct a correct rotation for the given extrinsic rotation (roll, pitch, yaw).

    const [roll, pitch, yaw] = rotation;
    const euler = new THREE.Euler(roll, pitch, yaw, 'ZYX');

    // imageYawOffset defines direction to the center of the image and depends on conventions
    // known camera types use 0 or 180 degrees (PI)
    const imageEuler = new THREE.Euler(0, 0, -(imageYawOffset ?? 0), 'ZYX');

    const layers = useMemo(() => {
      const layers0 = new THREE.Layers();
      if (opacity > 0.0001) {
        layers0.set(ViewerLayer.SphericalViewerLayer);
      }
      return layers0;
    }, [opacity]);

    const clickHandler = useCallback(
      (event: ThreeEvent<MouseEvent>) => {
        // event.intersections is an array of the meshes that are intersected by the mouse click.
        // the event listener is registered on the sphere that the image is textured onto, and
        // that mesh completely surrounds the camera. To get the correct intersection we need to
        // find the object that matches `meshReference.current` because there can be more than one mesh
        // intersected e.g. blister polygon.
        // (there seem to be two copies of the same spherical mesh in the array...maybe it's
        // an artifact of React).
        // .uv gives the texture coordinates of the intersection point on the mesh.
        const intersectionForThisImage = event.intersections.find(
          (intersection) => intersection.object === meshReference.current
        );
        if (intersectionForThisImage?.uv && intersectionForThisImage?.point) {
          let { x, y } = intersectionForThisImage.uv;
          // because the mesh has been rotated, the coordinates need to be adjusted as follows.
          x = 1 - x;
          y = 1 - y;

          if (onClick) {
            const { nativeEvent } = event;
            onClick({
              textureCoordinates: { x, y },
              worldCoordinates: intersectionForThisImage.point,
              event,
              nativeEvent,
              pickPointCloud,
            });
          }
        }
      },
      [onClick, pickPointCloud]
    );

    return (
      <group position={position} rotation={euler}>
        <group rotation={imageEuler}>
          <mesh
            ref={handleMesh}
            layers={layers}
            rotation={[Math.PI / 2, Math.PI / 2, 0]}
            scale={[sphereRadius, sphereRadius, sphereRadius]}
            geometry={viewerSphere}
            renderOrder={RENDER_ORDER_IMAGE_VIEWER}
            onClick={clickHandler}
            material={imageMaterial}
          />
        </group>
      </group>
    );
  }
);
SphericalImageViewer.displayName = 'SphericalImageViewer';
